import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bug, ChevronDown, ChevronRight } from "lucide-react";
import { useDiagnostics } from "@/hooks/useDiagnostics";
import { recentPolls } from "@/lib/devPollLog";
import type { DiagnosticsEvent } from "@lib/diagnostics";

const DEBUG_KEY = "vidquiz:dev:debug";

function videoIdFromPath(pathname: string): string | null {
  const match = /^\/(?:watch|quiz)\/([\w-]{11})/.exec(pathname);
  return match?.[1] ?? null;
}

function fmtMs(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return "";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(ms < 10_000 ? 2 : 1)}s`;
}

function fmtT(ms: number): string {
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`;
}

function isErrorEvent(ev: DiagnosticsEvent): boolean {
  return (
    ev.type.includes("error") ||
    ev.type.includes("refused") ||
    ev.type.includes("blocked") ||
    ev.type === "transcript.verdict" ||
    ev.type === "pipeline.failed" ||
    ev.type === "queue.retry" ||
    ev.type === "queue.failed" ||
    ev.type === "job.enqueue_failed"
  );
}

function Detail({ detail }: { detail: Record<string, unknown> | null }) {
  const [open, setOpen] = useState(false);
  if (!detail || Object.keys(detail).length === 0) return null;
  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
      >
        {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        detail ({Object.keys(detail).length})
      </button>
      {open && (
        <pre className="mt-1 max-h-56 overflow-auto rounded bg-muted p-2 text-[10px] leading-relaxed">
          {JSON.stringify(detail, null, 2)}
        </pre>
      )}
    </div>
  );
}

function EventRow({ ev }: { ev: DiagnosticsEvent }) {
  const error = isErrorEvent(ev);
  return (
    <li
      className={`rounded border px-1.5 py-1 text-[11px] leading-snug ${
        error ? "border-destructive/40 bg-destructive/5" : "border-border"
      }`}
    >
      <div className="flex items-baseline gap-1.5">
        <span className="shrink-0 font-mono text-[9px] text-muted-foreground">{fmtT(ev.t)}</span>
        <span
          className={`shrink-0 font-mono text-[9px] uppercase tracking-wide ${
            error ? "text-destructive" : "text-muted-foreground"
          }`}
        >
          {ev.type}
        </span>
        {ev.durationMs !== null && (
          <span className="ml-auto shrink-0 rounded bg-muted px-1 font-mono text-[9px] text-muted-foreground">
            {fmtMs(ev.durationMs)}
          </span>
        )}
      </div>
      <div className="mt-0.5 text-foreground">{ev.message}</div>
      <Detail detail={ev.detail} />
    </li>
  );
}

function Section({
  title,
  events,
}: {
  title: string;
  events: DiagnosticsEvent[];
}) {
  const [open, setOpen] = useState(true);
  if (events.length === 0) return null;
  return (
    <section className="space-y-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1 text-xs font-semibold text-muted-foreground"
      >
        {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        {title}
        <span className="ml-auto rounded-full bg-muted px-1.5 font-mono text-[10px]">
          {events.length}
        </span>
      </button>
      {open && <ul className="space-y-1">{events.map((ev) => <EventRow key={ev.seq} ev={ev} />)}</ul>}
    </section>
  );
}

function StageTimeline({ events }: { events: DiagnosticsEvent[] }) {
  const stages = useMemo(() => events.filter((ev) => ev.kind === "stage"), [events]);
  if (stages.length === 0) return null;
  return <Section title="Stage timing" events={stages} />;
}

function TranscriptView({ events }: { events: DiagnosticsEvent[] }) {
  const success = useMemo(() => {
    const found = [...events].reverse().find((ev) => ev.type === "transcript.success");
    return found?.detail as
      | { segmentCount?: number; chars?: number; language?: string | null; segments?: unknown[] }
      | undefined;
  }, [events]);

  const text = useMemo(() => {
    if (!success?.segments) return "";
    return (success.segments as Array<{ text?: string }>)
      .map((s) => s.text ?? "")
      .join(" ")
      .trim();
  }, [success]);

  if (!success || text.length === 0) return null;
  return (
    <section className="space-y-1">
      <div className="flex items-center gap-1 text-xs font-semibold text-muted-foreground">
        Transcript
        <span className="ml-auto rounded-full bg-muted px-1.5 font-mono text-[10px]">
          {success.segmentCount} seg · {success.chars?.toLocaleString("en-US")} chars
          {typeof success.language === "string" ? ` · ${success.language}` : ""}
        </span>
      </div>
      <pre className="max-h-64 overflow-auto rounded border bg-muted p-2 font-mono text-[10px] leading-relaxed text-foreground">
        {text}
      </pre>
    </section>
  );
}

function PollTimeline({ videoId }: { videoId: string | null }) {
  const polls = recentPolls().filter((p) => p.videoId === videoId);
  if (polls.length === 0) return null;
  const recent = polls.slice(-40);
  return (
    <section className="space-y-1">
      <div className="flex items-center gap-1 text-xs font-semibold text-muted-foreground">
        Client polls ({polls.length})
      </div>
      <pre className="max-h-40 overflow-auto rounded border bg-muted p-2 font-mono text-[10px] leading-relaxed">
        {recent.map((p, i) => {
          const gap = i === 0 ? 0 : p.at - recent[i - 1]!.at;
          return `${String(i + 1).padStart(2, " ")} ${fmtT(gap).padStart(6, " ")}  ${p.status.padEnd(22)}  +${p.latencyMs}ms\n`;
        }).join("")}
      </pre>
    </section>
  );
}

export function DevDiagnostics() {
  const { pathname } = useLocation();
  const videoId = useMemo(() => videoIdFromPath(pathname), [pathname]);
  const { enabled, available, startedAt, events } = useDiagnostics(videoId);
  const [open, setOpen] = useState(() => localStorage.getItem(DEBUG_KEY) === "1");

  if (!enabled) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    localStorage.setItem(DEBUG_KEY, next ? "1" : "0");
  };

  const jobs = events.filter((ev) => ev.kind === "job" || ev.kind === "queue");
  const pipeline = events.filter((ev) => ev.kind === "pipeline");
  const transcript = events.filter((ev) => ev.kind === "transcript");
  const model = events.filter((ev) => ev.kind === "model");
  const issues = events.filter(isErrorEvent);

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        aria-label="Toggle dev diagnostics"
        title="Dev diagnostics (local only)"
        className={`fixed left-1 top-1 z-[60] inline-flex size-7 items-center justify-center rounded-md border shadow-sm transition-colors ${
          open
            ? "border-destructive text-destructive hover:bg-destructive/10"
            : "border-border bg-background/80 text-muted-foreground hover:text-foreground"
        }`}
      >
        <Bug className="size-4" />
      </button>

      {open && (
        <aside className="fixed left-2 top-10 z-[60] w-[min(92vw,26rem)] overflow-y-auto rounded-lg border bg-background/95 p-3 shadow-xl backdrop-blur">
          <div className="flex items-center gap-2 border-b pb-2">
            <span className="text-xs font-semibold">Debug pipeline</span>
            <span className="font-mono text-[10px] text-muted-foreground">{videoId ?? "—"}</span>
            <span className="ml-auto rounded-full bg-muted px-1.5 font-mono text-[10px]">
              {events.length} events
            </span>
          </div>

          <div className="mt-2 space-y-2">
            {!videoId ? (
              <p className="text-[11px] text-muted-foreground">
                Open a /watch or /quiz page to inspect that video's pipeline.
              </p>
            ) : !available || startedAt === 0 ? (
              <p className="text-[11px] text-muted-foreground">
                {available
                  ? "Waiting for events — submit a URL to watch each generation step."
                  : "Diagnostics are only served by the local backend (JOB_MODE=inline). This build or mode exposes no events."}
              </p>
            ) : (
              <>
                <Section title="Job & queue" events={jobs} />
                <Section title="Pipeline" events={pipeline} />
                <StageTimeline events={events} />
                <Section title="YouTube requests" events={transcript} />
                <Section title="Model" events={model} />
                {issues.length > 0 && (
                  <Section title={`Issues (${issues.length})`} events={issues} />
                )}
                <TranscriptView events={events} />
                <PollTimeline videoId={videoId} />
              </>
            )}
          </div>
        </aside>
      )}
    </>
  );
}